import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ContractAnalyzer } from "@/components/ContractAnalyzer";
import { ArrowRight, ShieldCheck, ListChecks, AlertTriangle, CheckCircle2 } from "lucide-react";

type Scenario = {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  toolSlug: string;
  toolName: string;
  steps: { t: string; d: string; href?: string; hrefLabel?: string }[];
  redFlags: string[];
  greenFlags: string[];
};

const SCENARIOS: Record<string, Scenario> = {
  "signing-an-nda": {
    slug: "signing-an-nda",
    title: "Signing an NDA — What to Read Before You Sign",
    metaTitle: "Signing an NDA — What to Check First — legaldecoder",
    metaDescription:
      "Before you sign that NDA, here are the 5 clauses to check, the red flags to refuse, and what's industry-standard.",
    intro:
      "An NDA can be 1 page or 12. The same 5 clauses decide whether it's safe to sign — every time.",
    toolSlug: "nda-scanner",
    toolName: "NDA Scanner",
    steps: [
      {
        t: "Definition of confidential information",
        d: "Should be specific and marked — not 'all information of any kind.'",
        href: "/clauses/confidentiality-clause",
        hrefLabel: "Confidentiality clause →",
      },
      {
        t: "Duration",
        d: "2–5 years post-relationship is standard. 'In perpetuity' is a red flag.",
        href: "/clauses/confidentiality-clause",
        hrefLabel: "Confidentiality clause →",
      },
      {
        t: "Exceptions",
        d: "Public domain, prior knowledge, independent development, court-ordered disclosure — all four should be present.",
      },
      {
        t: "Hidden non-compete",
        d: "Restrictions on where you work next belong in their own clause — and may not be enforceable.",
        href: "/clauses/non-compete-clause",
        hrefLabel: "Non-compete clause →",
      },
      {
        t: "Whistleblower carve-out",
        d: "Federal and many state laws protect reporting illegal activity. The NDA should acknowledge this.",
      },
    ],
    redFlags: [
      "'Any information of any kind'",
      "Perpetual duration",
      "Embedded non-compete",
      "No standard exceptions",
      "One-sided remedies + attorney fees",
      "Tries to silence harassment claims",
    ],
    greenFlags: [
      "Specific definition",
      "2–5 year sunset",
      "Standard 4 exceptions",
      "Mutual obligations",
      "Whistleblower carve-out",
    ],
  },
  "freelance-contract-review": {
    slug: "freelance-contract-review",
    title: "Reviewing a Freelance Contract — A 5-Minute Checklist",
    metaTitle: "Freelance Contract Review — 5-Minute Checklist",
    metaDescription:
      "5 minutes, 5 clauses. The freelance-contract checklist that protects payment, IP, and your liability.",
    intro:
      "Most freelance contracts hide the same 5 risks. Here's how to find each one in 5 minutes.",
    toolSlug: "freelancer-protector",
    toolName: "Freelancer Protector",
    steps: [
      {
        t: "Payment terms",
        d: "Net-15 to net-30 with deposit and late-payment interest is fair. Net-60+ is a financing favor to the client.",
        href: "/clauses/payment-terms-clause",
        hrefLabel: "Payment terms clause →",
      },
      {
        t: "IP transfer",
        d: "Tie transfer to final payment received — not signing.",
        href: "/clauses/ip-assignment-clause",
        hrefLabel: "IP assignment clause →",
      },
      {
        t: "Indemnification",
        d: "Mutual, capped at fees paid, with carve-outs for gross negligence only.",
        href: "/clauses/indemnification-clause",
        hrefLabel: "Indemnification clause →",
      },
      {
        t: "Termination + kill fee",
        d: "14–30 day notice + 25–50% kill fee on cancelled milestones.",
        href: "/clauses/kill-fee-clause",
        hrefLabel: "Kill fee clause →",
      },
      {
        t: "Usage rights",
        d: "Define media, territory, duration, exclusivity. Charge an uplift for broad rights.",
        href: "/clauses/usage-rights-clause",
        hrefLabel: "Usage rights clause →",
      },
    ],
    redFlags: [
      "Net-90 with no deposit",
      "IP transfer at signing",
      "Uncapped one-sided indemnity",
      "No kill fee",
      "Worldwide perpetual exclusive rights for free",
      "Termination at any time",
    ],
    greenFlags: [
      "Net-15/30 + deposit",
      "IP on final payment",
      "Mutual capped indemnity",
      "25–50% kill fee",
      "Defined usage scope",
      "30-day notice termination",
    ],
  },
  "employment-offer-review": {
    slug: "employment-offer-review",
    title: "Reviewing a New Employment Offer — What to Check Before You Accept",
    metaTitle: "Employment Offer Review — What to Check First",
    metaDescription:
      "Before you sign your offer letter, decode the non-compete, arbitration, IP, and severance clauses that decide your future.",
    intro:
      "Salary is the easy part. The clauses around it shape your career for years after the job ends.",
    toolSlug: "employment-agreement-checker",
    toolName: "Employment Agreement Checker",
    steps: [
      {
        t: "Non-compete & non-solicit",
        d: "Cap duration at 6–12 months, narrow geography, and named competitors only.",
        href: "/clauses/non-compete-clause",
        hrefLabel: "Non-compete clause →",
      },
      {
        t: "Arbitration",
        d: "Push to remove class-action waivers and have employer pay all arbitration fees.",
        href: "/clauses/arbitration-clause",
        hrefLabel: "Arbitration clause →",
      },
      {
        t: "IP assignment",
        d: "Attach a Prior Inventions schedule and carve out side projects.",
        href: "/clauses/ip-assignment-clause",
        hrefLabel: "IP assignment clause →",
      },
      {
        t: "Severance & change-in-control",
        d: "Negotiate guaranteed severance on without-cause termination and acceleration on acquisition.",
        href: "/clauses/severance-clause",
        hrefLabel: "Severance clause →",
      },
      {
        t: "At-will + handbook",
        d: "Confirm what handbook policies actually apply.",
        href: "/clauses/at-will-employment-clause",
        hrefLabel: "At-will employment →",
      },
    ],
    redFlags: [
      "24-month nationwide non-compete",
      "Forced arbitration + class waiver",
      "IP grabs personal projects",
      "No severance",
      "Forfeits unvested equity",
    ],
    greenFlags: [
      "6–12 month, narrow non-compete",
      "Mutual arbitration, employer-paid fees",
      "Prior Inventions schedule attached",
      "2 weeks/year severance",
      "Acceleration on change of control",
    ],
  },
  "breaking-a-lease-early": {
    slug: "breaking-a-lease-early",
    title: "Breaking a Lease Early — How Much Will It Actually Cost?",
    metaTitle: "Breaking a Lease Early — What It Really Costs",
    metaDescription:
      "What an early termination clause actually means, what your landlord owes you, and how to minimize the damage if you have to leave.",
    intro: "Life moves. The early termination clause decides what it costs to move with it.",
    toolSlug: "lease-decoder",
    toolName: "Lease Decoder",
    steps: [
      {
        t: "Find the early termination clause",
        d: "Look for 'termination,' 'cancellation,' or 'breaking the lease.'",
        href: "/clauses/early-termination-clause",
        hrefLabel: "Early termination clause →",
      },
      {
        t: "Identify your fee structure",
        d: "Flat fee (1–2 months), liquidated damages, or 'all remaining rent' — they're very different bills.",
      },
      {
        t: "Check your state's mitigation rule",
        d: "Most states require landlords to try to re-rent. You usually owe rent only until they do.",
      },
      {
        t: "Look for exceptions",
        d: "Military relocation, domestic violence, uninhabitability, and (sometimes) job relocation can void the fee.",
      },
      {
        t: "Document everything in writing",
        d: "Send notice via email + certified mail. Keep photos of the unit at move-out.",
        href: "/clauses/security-deposit-clause",
        hrefLabel: "Security deposit clause →",
      },
    ],
    redFlags: [
      "Owe all remaining rent regardless of re-rental",
      "Deposit forfeited automatically",
      "No notice period",
      "Landlord has no duty to mitigate",
    ],
    greenFlags: [
      "Flat 1–2 month fee",
      "30–60 day notice",
      "Landlord must re-list promptly",
      "Deposit returned per the deposit clause",
    ],
  },
};

export const Route = createFileRoute("/scenarios/$slug")({
  head: ({ params }) => {
    const s = SCENARIOS[params.slug];
    if (!s) return { meta: [{ title: "Scenario not found — legaldecoder" }] };
    const url = `https://legaldecoder.io/scenarios/${params.slug}`;
    return {
      meta: [
        { title: s.metaTitle },
        { name: "description", content: s.metaDescription },
        { property: "og:title", content: s.metaTitle },
        { property: "og:description", content: s.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ScenarioPage,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Scenario not found</h1>
        <Link to="/" className="mt-4 inline-block text-primary-glow hover:underline">
          Home
        </Link>
      </div>
    </div>
  ),
});

function ScenarioPage() {
  const { slug } = Route.useParams();
  const s = SCENARIOS[slug];
  if (!s) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Scenario not found</h1>
          <Link to="/" className="mt-4 inline-block text-primary-glow hover:underline">
            Home
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{s.title}</h1>
            <p className="mt-4 text-lg text-muted-foreground">{s.intro}</p>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <ListChecks className="h-6 w-6 text-primary-glow" /> The checklist
          </h2>
          <ol className="mt-6 space-y-4">
            {s.steps.map((step, i) => (
              <li key={step.t} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <div className="flex items-start gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-glow text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold">{step.t}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{step.d}</p>
                    {step.href ? (
                      <Link
                        to={step.href as any}
                        className="mt-2 inline-block text-sm font-semibold text-primary-glow hover:underline"
                      >
                        {step.hrefLabel}
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-y border-border bg-surface/30 py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <AlertTriangle className="h-6 w-6 text-destructive" /> Red flags
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {s.redFlags.map((r) => (
                <li
                  key={r}
                  className="flex gap-2 rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-sm"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                  {r}
                </li>
              ))}
            </ul>
            <h2 className="mt-10 flex items-center gap-2 text-2xl font-bold">
              <CheckCircle2 className="h-6 w-6 text-success" /> Green flags
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {s.greenFlags.map((r) => (
                <li
                  key={r}
                  className="flex gap-2 rounded-xl border border-success/20 bg-success/5 p-3 text-sm"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-success" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-t border-border bg-gradient-glow py-14">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-primary-glow" />
              <h2 className="mt-3 text-3xl font-bold">Run your document through {s.toolName}</h2>
              <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
                Upload it and we'll flag every issue we just covered, in plain English.
              </p>
            </div>
            <div className="mt-8">
              <ContractAnalyzer toolSlug={s.toolSlug} />
            </div>
            <div className="mt-6 text-center">
              <Link
                to="/tools/$slug"
                params={{ slug: s.toolSlug }}
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary-glow hover:underline"
              >
                Or open the dedicated {s.toolName} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
        <p className="py-10 text-center text-xs uppercase tracking-wider text-muted-foreground">
          Not legal advice. For informational purposes only.
        </p>
        <Footer />
      </main>
    </div>
  );
}

export const SCENARIO_SLUGS = Object.keys(SCENARIOS);
